import React, { useEffect, useState, useCallback } from 'react';
import { X, Calendar, Clock, AlertCircle, CheckCircle, XCircle, Bell, User, FileText, MessageSquare, Wallet, DollarSign } from 'lucide-react';

export interface NotificationToastData {
  id: string;
  receiverId: string;
  type: string;
  title: string;
  message: string;
  metadata?: any;
  category?: string;
  createdAt: Date | string;
}

interface NotificationToastProps {
  notification: NotificationToastData;
  onClose: () => void;
  duration?: number; // Duration in milliseconds
}

const NotificationToast: React.FC<NotificationToastProps> = ({ 
  notification, 
  onClose, 
  duration = 7000 // Default 7 seconds
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Play subtle notification sound (if browser allows)
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Pleasant tone
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Very quiet
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {
      // Silently fail if audio context is not available (e.g., user hasn't interacted with page)
    }

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (duration / 100));
        return newProgress <= 0 ? 0 : newProgress;
      });
    }, 100);

    // Auto-dismiss after duration
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [duration]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  }, [onClose]);

  // Get icon and color based on notification type
  const getNotificationStyle = () => {
    const type = notification.type || '';
    const category = notification.category || '';

    if (type.includes('QUOTE_BUSINESS')) {
      return {
        icon: MessageSquare,
        iconColor: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
        iconBg: 'bg-indigo-100'
      };
    }
    if (type.includes('QUOTE_OFFER') || type.includes('QUOTE_REQUEST')) {
      return {
        icon: FileText,
        iconColor: type.includes('ACCEPTED') ? 'text-green-600' : 'text-indigo-600',
        bgColor: type.includes('ACCEPTED') ? 'bg-green-50' : 'bg-indigo-50',
        borderColor: type.includes('ACCEPTED') ? 'border-green-200' : 'border-indigo-200',
        iconBg: type.includes('ACCEPTED') ? 'bg-green-100' : 'bg-indigo-100'
      };
    }
    if (type.includes('APPOINTMENT_DEPOSIT_REQUESTED') || type.includes('DEPOSIT_REQUESTED')) {
      return {
        icon: DollarSign,
        iconColor: 'text-amber-500',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-100'
      };
    }
    if (type.includes('APPOINTMENT_BOOKED') || type.includes('BOOKED')) {
      return {
        icon: Calendar,
        iconColor: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconBg: 'bg-blue-100'
      };
    }
    if (type.includes('CANCELLED') || type.includes('CANCEL')) {
      return {
        icon: XCircle,
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconBg: 'bg-red-100'
      };
    }
    if (type.includes('CONFIRMED') || type.includes('CONFIRM')) {
      return {
        icon: CheckCircle,
        iconColor: 'text-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconBg: 'bg-green-100'
      };
    }
    if (type.includes('RESCHEDULE') || type.includes('RESCHEDULED')) {
      return {
        icon: Clock,
        iconColor: 'text-amber-500',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-100'
      };
    }
    if (type.includes('DEPOSIT')) {
      return {
        icon: Wallet,
        iconColor: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
      };
    }
    if (type.includes('INVOICE')) {
      const isPaid = type.includes('PAID') || type.includes('PAYMENT_RECEIVED');
      return {
        icon: isPaid ? CheckCircle : FileText,
        iconColor: isPaid ? 'text-emerald-600' : 'text-amber-600',
        bgColor: isPaid ? 'bg-emerald-50' : 'bg-amber-50',
        borderColor: isPaid ? 'border-emerald-200' : 'border-amber-200',
        iconBg: isPaid ? 'bg-emerald-100' : 'bg-amber-100',
      };
    }
    if (category === 'REVIEW' || type.includes('REVIEW')) {
      return {
        icon: AlertCircle,
        iconColor: 'text-purple-500',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        iconBg: 'bg-purple-100'
      };
    }
    
    // Default
    return {
      icon: Bell,
      iconColor: 'text-gray-500',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100'
    };
  };

  const style = getNotificationStyle();
  const Icon = style.icon;

  // Format time if metadata has scheduledAt
  const formatTime = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return null;
    }
  };

  const scheduledTime = notification.metadata?.scheduledAt 
    ? formatTime(notification.metadata.scheduledAt)
    : null;

  const invoicePaidToastBody = (): string => {
    if (notification.type !== 'INVOICE_PAYMENT_RECEIVED') return '';
    const trimmed = String(notification.message || '').trim();
    if (trimmed) return trimmed;
    const m = notification.metadata || {};
    const cust = String(m.customerName ?? m.customer_name ?? '').trim() || 'Customer';
    const inv = String(m.invoiceNumber ?? m.invoice_number ?? '').trim();
    const amtRaw = m.amountPaid ?? m.amount_paid;
    const amt = typeof amtRaw === 'number' && Number.isFinite(amtRaw) ? amtRaw : NaN;
    if (inv && !Number.isNaN(amt)) return `${cust} paid ${inv} for $${amt.toFixed(2)}.`;
    if (!Number.isNaN(amt)) return `${cust} paid $${amt.toFixed(2)}.`;
    if (inv) return `${cust} paid invoice ${inv}.`;
    return `${cust} paid an invoice.`;
  };

  const displayMessage =
    notification.type === 'INVOICE_PAYMENT_RECEIVED'
      ? invoicePaidToastBody() || notification.message
      : notification.message;

  return (
    <div
      className={`
        relative w-full max-w-sm mb-3
        transform transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${isExiting ? 'translate-x-full opacity-0' : ''}
      `}
      style={{
        animation: isVisible && !isExiting ? 'slideInRight 0.3s ease-out' : undefined
      }}
    >
      <div
        className={`
          ${style.bgColor} ${style.borderColor}
          border-l-4 rounded-xl shadow-2xl
          p-5 flex items-start gap-4
          hover:shadow-2xl transition-all duration-200
          backdrop-blur-sm
          relative overflow-hidden
          ${isVisible && !isExiting ? 'animate-pulse-once' : ''}
          cursor-pointer
        `}
        style={{
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}
        onClick={(e) => {
          // Prevent closing when clicking on the notification content
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('button')) {
            return;
          }
        }}
      >
        {/* Progress bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1 bg-gray-200"
          style={{ zIndex: 1 }}
        >
          <div 
            className={`h-full transition-all duration-100 ease-linear ${style.iconColor.replace('text-', 'bg-')}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        {/* Icon */}
        <div className={`
          ${style.iconBg} ${style.iconColor}
          rounded-full p-3.5 flex-shrink-0
          flex items-center justify-center
          shadow-lg
          ${isVisible && !isExiting ? 'animate-bounce-subtle' : ''}
          ring-2 ring-white/50
        `}>
          <Icon className="w-7 h-7" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-900 mb-1.5">
                {notification.title}
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed font-medium">
                {displayMessage}
              </p>
              
              {/* Additional info from metadata */}
              {notification.type !== 'INVOICE_PAYMENT_RECEIVED' && notification.metadata?.customerName && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
                  <User className="w-3.5 h-3.5" />
                  <span>{notification.metadata.customerName}</span>
                </div>
              )}
              
              {scheduledTime && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{scheduledTime}</span>
                </div>
              )}

              {notification.metadata?.serviceName && (
                <div className="mt-1.5 text-xs text-gray-600">
                  <span className="font-medium">Service:</span> {notification.metadata.serviceName}
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="
                flex-shrink-0
                text-gray-400 hover:text-gray-600
                transition-colors duration-200
                p-1 rounded-full hover:bg-white/50
              "
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes pulse-once {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }
        .animate-pulse-once {
          animation: pulse-once 0.6s ease-in-out;
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationToast;

